/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Posterize Node
   Color channel quantization into discrete step bands.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const POSTERIZE_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform float u_levels;

void main() {
  vec4 src = texture(u_image, v_uv);
  float n = max(2.0, u_levels);
  vec3 posterized = floor(src.rgb * n) / (n - 1.0);
  fragColor = vec4(clamp(posterized, 0.0, 1.0), src.a);
}
`;

export const posterizeNode: NodeDefinition = {
  type: 'color.posterize',
  category: 'Color',
  label: 'Posterize',
  description: 'Reduces the number of tonal color levels for a stylized print effect',
  keywords: ['posterize', 'quantize', 'bands', 'retro', 'color'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Output', type: 'image' },
  ],
  params: [
    {
      id: 'levels',
      name: 'Tonal Levels',
      type: 'slider',
      defaultValue: 4,
      min: 2,
      max: 32,
      step: 1,
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: POSTERIZE_SHADER,
    getUniforms: (inputs, params) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_levels: Number(params.levels) || 4,
    }),
  }),
};
